/**
 * As quatro grandezas que a tela mostra, definidas UMA vez.
 *
 * Responsabilidade: dizer, por metrica, de onde vem o numero (telemetria §4 e
 * digital twin §5), como se chama, em que unidade e com quantas casas.
 *
 * NAO e responsabilidade deste arquivo: desenhar. O cartao de leitura e o
 * grafico desenham coisas bem diferentes, mas leem a MESMA definicao —
 * antes disto, o rotulo e o numero de casas viviam dentro de `leituras.ts`, e
 * um grafico com a sua propria tabela acabaria mostrando "1.2 kPa" onde o
 * cartao mostra "1.20".
 */

import type { EstadoAparelho, TelemetrySample } from "./tipos";
import { inteiro, num } from "./ui";

type SensorDoTwin = NonNullable<EstadoAparelho["sensor"]>;

export interface Metrica {
  /** Estavel: e o que o `store` guarda como metrica aberta. */
  chave: "temp" | "rh" | "co2" | "vpd";
  rotulo: string;
  unidade: string;
  casas: number;
  de: (a: TelemetrySample) => number | null;
  doTwin: (s: SensorDoTwin) => number | null;
  /**
   * Abaixo deste delta (na mesma unidade), o cartao mostra "→" e nao uma seta
   * com numero — ruido de sensor nao e tendencia. Calibrado por olho, uma
   * casa acima da resolucao que `formatar()` exibe.
   */
  limiarTendencia: number;
}

export const METRICAS: Metrica[] = [
  {
    chave: "temp",
    rotulo: "Temperatura",
    unidade: "°C",
    casas: 1,
    de: (a) => a.temp_c,
    doTwin: (s) => s.temp_c ?? null,
    limiarTendencia: 0.2,
  },
  {
    chave: "rh",
    rotulo: "Umidade",
    unidade: "%",
    casas: 1,
    de: (a) => a.rh_pct,
    doTwin: (s) => s.rh_pct ?? null,
    limiarTendencia: 1,
  },
  {
    chave: "co2",
    rotulo: "CO₂",
    unidade: "ppm",
    casas: 0,
    de: (a) => a.co2_ppm,
    doTwin: (s) => s.co2_ppm ?? null,
    limiarTendencia: 20,
  },
  {
    chave: "vpd",
    rotulo: "VPD",
    unidade: "kPa",
    casas: 2,
    de: (a) => a.vpd_kpa,
    doTwin: (s) => s.vpd_kpa ?? null,
    limiarTendencia: 0.05,
  },
];

export const acharMetrica = (chave: string | null): Metrica | null =>
  METRICAS.find((m) => m.chave === chave) ?? null;

/** "—" para ausente, nunca zero (contrato §4). */
export const formatar = (metrica: Metrica, valor: number | null): string =>
  metrica.casas === 0 ? inteiro(valor) : num(valor, metrica.casas);

/** Delta com sinal explicito ("+0.6" / "-1.2"), nas mesmas casas do valor grande. */
export function formatarDelta(metrica: Metrica, delta: number): string {
  const grandeza = formatar(metrica, Math.abs(delta));
  if (grandeza === "—") return grandeza;
  if (delta > 0) return `+${grandeza}`;
  if (delta < 0) return `-${grandeza}`;
  return grandeza;
}

/**
 * Nomes de estagio do firmware (`plant_stage`, §5) em portugues.
 *
 * Mesmas quatro etiquetas do painel local — a faixa ideal de VPD e derivada do
 * estagio, entao dizer qual estagio e faz parte de explicar a faixa.
 */
const ESTAGIOS: Record<string, string> = {
  seedling: "muda/clone",
  veg: "vegetativo",
  flowering: "floração",
  late_flowering: "floração tardia",
};

/** Devolve `null` para estagio nao reportado ou desconhecido — nunca inventa. */
export const nomeDoEstagio = (estagio: string | undefined): string | null =>
  estagio === undefined ? null : (ESTAGIOS[estagio] ?? estagio);
